/**
 * Shared commission validation utilities.
 *
 * Centralises the "total commission percentage must not exceed 100%" logic
 * that was previously duplicated across QuickAssignCard (form + mutation modes),
 * new-claim-modal, and edit-claim-modal.
 */

// ─── Percentage-based validation ──────────────────────────────────────────────

/**
 * Calculate the total commission percentage across all percent-type roles.
 *
 * Only roles whose fee type is `"percent"` (or defaults to it) are included.
 * Non-percent fee types (flat_rate, hourly) are excluded from the total.
 */
export function computeTotalCommissionPercentage(
  percentages: Record<string, string>,
  feeTypes: Record<string, string>,
): number {
  return Object.entries(percentages).reduce((sum, [role, val]) => {
    const ft = feeTypes[role] || "percent";
    if (ft !== "percent") return sum;
    const n = parseFloat(val);
    return sum + (isNaN(n) ? 0 : n);
  }, 0);
}

/**
 * Calculate the total commission percentage across all percent-type roles from an assignments array.
 *
 * Only roles whose fee type is `"percent"` (or defaults to it) are included.
 * Non-percent fee types (flat_rate, hourly) are excluded from the total.
 */
export function computeArrayCommissionPercentage(
  assignments: any[]
): number {
  return assignments.reduce((sum, a) => {
    const ft = a.commissionFeeType || "percent";
    if (ft !== "percent") return sum;
    const n = parseFloat(a.commissionPercentage || "0");
    return sum + (isNaN(n) ? 0 : n);
  }, 0);
}

/** Maximum allowed total commission percentage. */
export const MAX_COMMISSION_PERCENTAGE = 100;

/**
 * Returns `true` when the total percent-type commission exceeds 100%.
 */
export function isCommissionPercentageExceeded(
  percentages: Record<string, string>,
  feeTypes: Record<string, string>,
): boolean {
  return computeTotalCommissionPercentage(percentages, feeTypes) > MAX_COMMISSION_PERCENTAGE;
}

// ─── Amount-based validation ──────────────────────────────────────────────────

/**
 * Returns `true` when a single commission amount exceeds the distributable pool.
 *
 * Used in the commission distribution edit dialog to prevent amounts greater
 * than the company fee / check amount.
 */
export function isCommissionAmountExceeded(
  amount: number,
  maxAmount: number,
): boolean {
  return maxAmount > 0 && amount > maxAmount;
}
